import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Tooltip, Legend, Title,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import {
  getBusinesses, getClients, getBizServices,
  getInvoices, getInvoice, createInvoice, updateInvoice, deleteInvoice,
  approveInvoice, markSentInvoice, logPayment, downloadInvoicePdf,
  searchTransactions, getFiscalYearSettings, getTransactionFYYears,
} from '../../api'
import { SortableTh, multiSortBy, promoteSort } from '../../components/txDisplay'
import type { SortKey } from '../../components/txDisplay'
import type { Invoice, InvoiceLineItem, Client, BizService, Business, Transaction, FiscalYearSettings } from '../../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title)

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number) => n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : ''

// ── FY helpers (mirrors BizTransactions) ────────────────────────────────────────
function getFiscalYear(dateStr: string, fy: FiscalYearSettings): number {
  if (fy.followsCalendar) return new Date(dateStr).getFullYear()
  const d = new Date(dateStr + 'T12:00:00')
  const month = d.getMonth() + 1; const day = d.getDate(); const year = d.getFullYear()
  if (month > fy.startMonth || (month === fy.startMonth && day >= fy.startDay)) return year + 1
  return year
}
function getFYDateRange(fyYear: number, fy: FiscalYearSettings): [string, string] {
  if (fy.followsCalendar) return [`${fyYear}-01-01`, `${fyYear}-12-31`]
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateFrom = `${fyYear - 1}-${pad(fy.startMonth)}-${pad(fy.startDay)}`
  const endD = new Date(fyYear, fy.startMonth - 1, fy.startDay - 1)
  return [dateFrom, endD.toISOString().slice(0, 10)]
}
function fyLabel(fyYear: number, followsCalendar: boolean): string {
  return followsCalendar ? String(fyYear) : `FY ${fyYear}`
}

// Authoritative paid date: from the linked transaction when present, else the invoice's own paid date.
function paidDateOf(inv: Invoice): string | null {
  return inv.paymentTransactionId ? inv.paidTransactionDate : inv.paidDate
}
// Fiscal year of a (possibly datetime) date string — normalizes to YYYY-MM-DD first
// so getFiscalYear's non-calendar 'T12:00:00' append doesn't produce NaN on ISO datetimes.
function fyOf(dateStr: string | null, fy: FiscalYearSettings): number | null {
  if (!dateStr) return null
  const n = getFiscalYear(dateStr.slice(0, 10), fy)
  return Number.isNaN(n) ? null : n
}
// Paid amount: from the linked transaction when present, else the invoice total (fallback).
function paidAmountOf(inv: Invoice): number | null {
  if (inv.status !== 'Paid') return null
  if (inv.paymentTransactionId) return inv.paidTransactionAmount
  return calcTotals(inv.lineItems ?? [], inv.taxRate).total
}

// Generate ordered month buckets spanning [from, to] inclusive.
function monthBuckets(from: string, to: string): { key: string; label: string }[] {
  const start = new Date(from + 'T12:00:00')
  const end = new Date(to + 'T12:00:00')
  const out: { key: string; label: string }[] = []
  const d = new Date(start.getFullYear(), start.getMonth(), 1)
  while (d <= end && out.length < 60) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    out.push({ key, label: d.toLocaleDateString('en-CA', { month: 'short', year: '2-digit' }) })
    d.setMonth(d.getMonth() + 1)
  }
  return out
}

const CLIENT_COLORS = [
  'rgba(99,102,241,0.7)', 'rgba(16,185,129,0.7)', 'rgba(251,191,36,0.7)',
  'rgba(59,130,246,0.7)', 'rgba(236,72,153,0.7)', 'rgba(244,114,182,0.7)',
  'rgba(45,212,191,0.7)', 'rgba(168,85,247,0.7)', 'rgba(248,113,113,0.7)',
  'rgba(132,204,22,0.7)',
]

function autoCalcDueDate(invoiceDate: string, terms: string): string | null {
  const match = terms.match(/\d+/)
  if (!match) return null
  const days = parseInt(match[0])
  const d = new Date(invoiceDate + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function statusBadge(status: string): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    Draft:    ['rgba(148,163,184,0.15)', 'var(--pb-muted)'],
    Approved: ['rgba(251,191,36,0.15)',  '#b45309'],
    Sent:     ['rgba(59,130,246,0.15)',  '#1d4ed8'],
    Paid:     ['rgba(16,185,129,0.15)',  '#059669'],
    Overdue:  ['rgba(239,68,68,0.15)',   '#dc2626'],
  }
  const [bg, color] = map[status] ?? ['rgba(148,163,184,0.15)', 'var(--pb-muted)']
  return { display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.65rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: bg, color }
}

function calcTotals(items: Partial<InvoiceLineItem>[], taxRate: number) {
  const subtotal = items.reduce((s, li) => s + (li.quantity ?? 1) * (li.rate ?? 0), 0)
  const tax = items.filter(li => li.isTaxable).reduce((s, li) => s + (li.quantity ?? 1) * (li.rate ?? 0), 0) * taxRate
  return { subtotal, tax, total: subtotal + tax }
}

// ── Shared input style ────────────────────────────────────────────────────────
const inputCls = 'pb-input'

// ── Paper palette (mirrors InvoicePdfDocument.cs) ──────────────────────────────
// The on-screen "sheet" renders identically in light and dark mode so it reads
// as a true preview of the exported PDF.
const PP = {
  ink: '#101321', muted: '#5d6472', line: '#dfe3f2', surfaceSoft: '#eef1ff',
  brand: '#7177ee', brandDeep: '#484f9d', brandDark: '#252a6b', paper: '#f7f8fc', white: '#ffffff',
} as const

// Inline-edit affordance: tinted background + dashed underline so the user can
// see which parts of the paper are editable without leaving the document view.
function pf(editable: boolean, extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    fontFamily: 'inherit', color: 'inherit', border: 'none', outline: 'none',
    background: editable ? 'rgba(113,119,238,0.08)' : 'transparent',
    boxShadow: editable ? `inset 0 -1px 0 ${PP.brand}` : 'none',
    borderRadius: 4, padding: editable ? '2px 5px' : 0, ...extra,
  }
}

// ── Invoice Paper — faithful on-screen rendition of the exported PDF ───────────
interface PaperProps {
  editable: boolean
  inv: Partial<Invoice>
  lines: Partial<InvoiceLineItem>[]
  business?: Business
  clients?: Client[]
  services?: BizService[]
  invoiceNumber?: string
  set?: (k: keyof Invoice, v: unknown) => void
  setLine?: (i: number, k: keyof InvoiceLineItem, v: unknown) => void
  pickService?: (i: number, id: number) => void
  addLine?: () => void
  removeLine?: (i: number) => void
}

function InvoicePaper({ editable, inv, lines, business, clients, services, invoiceNumber, set, setLine, pickService, addLine, removeLine }: PaperProps) {
  const biz = business
  const client = inv.client ?? clients?.find(c => c.clientID === inv.clientID)
  const storedTaxRate = inv.taxRate ?? 0
  const { subtotal, tax, total } = calcTotals(lines, storedTaxRate)
  const today = new Date().toISOString().slice(0, 10)

  const panel: React.CSSProperties = { flex: 1, background: PP.white, border: `1px solid ${PP.line}`, borderRadius: 8, padding: 16 }
  const sectLabel: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', color: PP.brandDark }
  const muted: React.CSSProperties = { fontSize: 12, color: PP.muted, marginTop: 3 }

  const metaRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: PP.muted, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: PP.ink, textAlign: 'right' }}>{value}</span>
    </div>
  )

  // Client address block (read-only — derived from the selected client record)
  const clientLines = client ? [
    client.contactName,
    client.address1,
    client.address2,
    [client.city, client.province].filter(Boolean).join(', ') + (client.postalCode ? ` ${client.postalCode}` : ''),
    client.email,
  ].map(s => (s ?? '').trim()).filter(Boolean) : []

  const bizLines = biz ? [
    biz.contactName,
    biz.address1,
    biz.address2,
    [biz.city, biz.province].filter(Boolean).join(', ') + (biz.postalCode ? ` ${biz.postalCode}` : ''),
    biz.country,
    biz.phone,
    biz.email,
  ].map(s => (s ?? '').trim()).filter(Boolean) : []

  const showNotes = editable || !!inv.notes || !!inv.paymentTerms
  const showProject = editable || !!inv.projectName
  const orderedLines = editable ? lines : [...lines].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

  const thBase: React.CSSProperties = { color: PP.white, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '11px 12px' }

  return (
    <div style={{ maxWidth: 850, margin: '0 auto', background: PP.paper, borderRadius: 14, boxShadow: '0 10px 34px rgba(16,19,33,0.14)', padding: '42px 46px', color: PP.ink, fontSize: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {biz?.logoPath && <img src={biz.logoPath} alt="" style={{ width: 56, maxHeight: 56, objectFit: 'contain', flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: PP.ink, lineHeight: 1.2 }}>{biz?.businessName}</div>
              {biz?.tagLine && <div style={{ fontSize: 11.5, fontWeight: 700, color: PP.muted, marginTop: 2 }}>{biz.tagLine}</div>}
            </div>
          </div>
          <div style={{ fontSize: 46, fontWeight: 800, color: PP.brandDark, marginTop: 12, lineHeight: 1 }}>Invoice</div>
        </div>
        <div style={{ width: 212, flexShrink: 0 }}>
          {metaRow('Invoice #', invoiceNumber
            ? invoiceNumber
            : <span style={{ color: PP.muted, fontWeight: 600 }}>Auto-assigned</span>)}
          {metaRow('Issued', editable
            ? <input type="date" value={inv.date?.slice(0, 10) ?? today} onChange={e => set?.('date', e.target.value)} style={pf(true, { fontSize: 11, fontWeight: 700, textAlign: 'right' })} />
            : fmtDate(inv.date))}
          {(editable || inv.dueDate) && metaRow('Due', editable
            ? <input type="date" value={inv.dueDate?.slice(0, 10) ?? ''} onChange={e => set?.('dueDate', e.target.value || null)} style={pf(true, { fontSize: 11, fontWeight: 700, textAlign: 'right' })} />
            : fmtDate(inv.dueDate))}
          {(editable || inv.paymentTerms) && metaRow('Terms', editable
            ? <input value={inv.paymentTerms ?? ''} placeholder="Net 30" onChange={e => set?.('paymentTerms', e.target.value)} style={pf(true, { fontSize: 11, fontWeight: 700, textAlign: 'right', width: 110 })} />
            : inv.paymentTerms)}
        </div>
      </div>
      <div style={{ height: 1, background: PP.line, marginTop: 18 }} />

      {/* Parties */}
      <div style={{ display: 'flex', gap: 14, marginTop: 22 }}>
        {/* FROM — business, always read-only */}
        <div style={panel}>
          <div style={sectLabel}>FROM</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: PP.ink, marginTop: 10 }}>{biz?.businessName}</div>
          {bizLines.map((l, i) => <div key={i} style={muted}>{l}</div>)}
        </div>
        {/* BILL TO — client picker in edit mode */}
        <div style={panel}>
          <div style={sectLabel}>BILL TO</div>
          {editable ? (
            <>
              <select value={inv.clientID ?? ''} onChange={e => set?.('clientID', e.target.value ? parseInt(e.target.value) : null)}
                style={pf(true, { fontSize: 14, fontWeight: 700, color: client ? PP.ink : PP.muted, marginTop: 8, width: '100%', cursor: 'pointer' })}>
                <option value="">Select a client…</option>
                {clients?.map(c => <option key={c.clientID} value={c.clientID}>{c.clientNumber} — {c.clientName}</option>)}
              </select>
              {clientLines.map((l, i) => <div key={i} style={muted}>{l}</div>)}
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: PP.ink, marginTop: 10 }}>{client?.clientName}</div>
              {clientLines.map((l, i) => <div key={i} style={muted}>{l}</div>)}
            </>
          )}
        </div>
      </div>

      {/* Project strip */}
      {showProject && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginTop: 14, background: PP.surfaceSoft, border: `1px solid ${PP.brand}`, borderRadius: 8, padding: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editable ? (
              <>
                <input value={inv.projectName ?? ''} placeholder="Project / engagement name" onChange={e => set?.('projectName', e.target.value)}
                  style={pf(true, { fontSize: 14, fontWeight: 700, color: PP.ink, width: '100%' })} />
                <input value={inv.projectDescription ?? ''} placeholder="Brief description of work (optional)" onChange={e => set?.('projectDescription', e.target.value)}
                  style={pf(true, { fontSize: 12, color: PP.muted, width: '100%', marginTop: 5 })} />
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: PP.ink }}>{inv.projectName}</div>
                {inv.projectDescription && <div style={{ fontSize: 12, color: PP.muted, marginTop: 3 }}>{inv.projectDescription}</div>}
              </>
            )}
          </div>
          {(editable || inv.projectTag) && (
            editable
              ? <input value={inv.projectTag ?? ''} placeholder="Tag" onChange={e => set?.('projectTag', e.target.value)}
                  style={pf(true, { fontSize: 11, fontWeight: 700, color: PP.brandDeep, background: PP.white, width: 110, flexShrink: 0, textAlign: 'center', boxShadow: 'none' })} />
              : <span style={{ background: PP.white, padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: PP.brandDeep, flexShrink: 0 }}>{inv.projectTag}</span>
          )}
        </div>
      )}

      {/* Line items */}
      <div style={{ marginTop: 14, background: PP.white, border: `1px solid ${PP.line}`, borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: PP.brandDark }}>
              <th style={{ ...thBase, textAlign: 'left' }}>Description</th>
              <th style={{ ...thBase, textAlign: 'right', width: '11%' }}>Qty</th>
              <th style={{ ...thBase, textAlign: 'right', width: '17%' }}>Rate</th>
              <th style={{ ...thBase, textAlign: 'right', width: '18%' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {orderedLines.map((li, i) => {
              const amount = (li.quantity ?? 0) * (li.rate ?? 0)
              const rowBg = i % 2 === 0 ? PP.white : PP.paper
              const cell: React.CSSProperties = { padding: '11px 12px', borderBottom: i === orderedLines.length - 1 ? 'none' : `0.5px solid ${PP.line}`, verticalAlign: 'top', background: rowBg }
              return (
                <tr key={i}>
                  <td style={cell}>
                    {editable && (
                      <select value={li.serviceID ?? ''} onChange={e => { const v = parseInt(e.target.value); if (v) pickService?.(i, v) }}
                        style={pf(true, { fontSize: 10.5, color: PP.brandDeep, fontWeight: 600, marginBottom: 4, cursor: 'pointer', display: 'block' })}>
                        <option value="">+ from a service…</option>
                        {services?.map(s => <option key={s.serviceID} value={s.serviceID}>{s.serviceName}</option>)}
                      </select>
                    )}
                    {editable ? (
                      <>
                        <input value={li.title ?? ''} placeholder="Description" onChange={e => setLine?.(i, 'title', e.target.value)}
                          style={pf(true, { fontSize: 14, fontWeight: 700, color: PP.ink, width: '100%' })} />
                        <input value={li.detail ?? ''} placeholder="Detail (optional)" onChange={e => setLine?.(i, 'detail', e.target.value)}
                          style={pf(true, { fontSize: 12, color: PP.muted, width: '100%', marginTop: 4 })} />
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700, color: PP.ink }}>{li.title}</div>
                        {li.detail && <div style={{ fontSize: 12, color: PP.muted, marginTop: 3 }}>{li.detail}</div>}
                      </>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: 'right' }}>
                    {editable
                      ? <input type="number" step="0.5" min="0" value={li.quantity ?? 1} onChange={e => setLine?.(i, 'quantity', parseFloat(e.target.value) || 0)}
                          style={pf(true, { fontSize: 14, textAlign: 'right', width: '100%' })} />
                      : <span style={{ fontSize: 14 }}>{li.quantity}</span>}
                  </td>
                  <td style={{ ...cell, textAlign: 'right' }}>
                    {editable
                      ? <input type="number" step="0.01" min="0" value={li.rate ?? 0} onChange={e => setLine?.(i, 'rate', parseFloat(e.target.value) || 0)}
                          style={pf(true, { fontSize: 14, textAlign: 'right', width: '100%' })} />
                      : <span style={{ fontSize: 14 }}>{fmt(li.rate ?? 0)}</span>}
                  </td>
                  <td style={{ ...cell, textAlign: 'right' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{fmt(amount)}</span>
                    {editable && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                        <label style={{ fontSize: 10.5, color: PP.muted, display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!li.isTaxable} onChange={e => setLine?.(i, 'isTaxable', e.target.checked)} />HST
                        </label>
                        {orderedLines.length > 1 && (
                          <button onClick={() => removeLine?.(i)} title="Remove line"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 0, fontSize: 12, lineHeight: 1 }}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {editable && (
          <div style={{ padding: 10, borderTop: `0.5px solid ${PP.line}` }}>
            <button onClick={() => addLine?.()}
              style={{ background: 'none', border: `1px dashed ${PP.brand}`, color: PP.brandDeep, cursor: 'pointer', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>
              <i className="bi bi-plus-lg me-1" style={{ fontSize: 11 }} />Add line
            </button>
          </div>
        )}
      </div>

      {/* Notes + Totals */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginTop: 14 }}>
        {showNotes && (
          <div style={{ ...panel }}>
            <div style={sectLabel}>NOTES &amp; TERMS</div>
            {editable ? (
              <textarea value={inv.notes ?? ''} placeholder="Payment terms, thank-you note, or any other notes…" onChange={e => set?.('notes', e.target.value)}
                style={pf(true, { fontSize: 12, color: PP.muted, width: '100%', minHeight: 70, marginTop: 10, resize: 'vertical', display: 'block' })} />
            ) : (
              <>
                {inv.notes && <div style={{ fontSize: 12, color: PP.muted, marginTop: 10, whiteSpace: 'pre-wrap' }}>{inv.notes}</div>}
                {inv.paymentTerms && <div style={{ fontSize: 12, color: PP.muted, marginTop: 6 }}>Payment terms: {inv.paymentTerms}.</div>}
              </>
            )}
          </div>
        )}
        <div style={{ width: 250, flexShrink: 0, background: PP.white, border: `1px solid ${PP.brand}`, borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: PP.muted, marginBottom: 6 }}>
            <span>Subtotal</span><span>{fmt(subtotal)}</span>
          </div>
          {biz?.hstNumber && <div style={{ fontSize: 10.5, color: PP.muted, marginBottom: 6 }}>HST/GST Reg #: {biz.hstNumber}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: PP.muted, marginBottom: 6 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Tax / HST (
              {editable
                ? <input type="number" step="0.1" min="0" max="100" value={(storedTaxRate * 100).toFixed(2).replace(/\.?0+$/, '')}
                    onChange={e => set?.('taxRate', (parseFloat(e.target.value) || 0) / 100)}
                    style={pf(true, { fontSize: 12, width: 44, textAlign: 'right' })} />
                : <span>{(storedTaxRate * 100).toFixed(storedTaxRate * 100 % 1 ? 2 : 0)}</span>}
              %)
            </span>
            <span>{fmt(tax)}</span>
          </div>
          <div style={{ height: 1, background: PP.line, margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: PP.brandDark }}>Total Due ({inv.currency ?? 'CAD'})</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: PP.brandDark }}>{fmt(total)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Invoice Form ──────────────────────────────────────────────────────────────
type FormMode = 'create' | 'edit'
type View = 'list' | 'detail' | 'create' | 'edit'

interface InvoiceFormProps {
  mode: FormMode
  businessId: number
  business?: Business
  clients: Client[]
  services: BizService[]
  initial?: Invoice
  onSaved: () => void
  onCancel: () => void
  taxRate: number
  defaultPaymentTerms?: string
  defaultPaymentMethod?: string
}

function InvoiceForm({ mode, businessId, business, clients, services, initial, onSaved, onCancel, taxRate, defaultPaymentTerms, defaultPaymentMethod }: InvoiceFormProps) {
  const today = new Date().toISOString().slice(0, 10)
  const initTerms = initial?.paymentTerms ?? defaultPaymentTerms ?? ''
  const initDue   = initial?.dueDate?.slice(0, 10) ?? autoCalcDueDate(today, initTerms) ?? ''

  const [inv, setInv] = useState<Partial<Invoice>>(initial ?? {
    businessID: businessId, date: today, dueDate: initDue,
    status: 'Draft', currency: 'CAD', taxRate,
    paymentTerms: initTerms, paymentMethod: defaultPaymentMethod ?? '',
  })
  const [lines, setLines] = useState<Partial<InvoiceLineItem>[]>(
    initial?.lineItems?.length
      ? initial.lineItems
      : [{ serviceID: null, title: '', detail: '', quantity: 1, rate: 0, isTaxable: true, sortOrder: 0 }]
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (k: keyof Invoice, v: unknown) => setInv(f => ({ ...f, [k]: v }))
  const addLine = () => setLines(ls => [...ls, { serviceID: null, title: '', detail: '', quantity: 1, rate: 0, isTaxable: true, sortOrder: ls.length }])
  const removeLine = (i: number) => setLines(ls => ls.filter((_, idx) => idx !== i))
  const setLine = (i: number, k: keyof InvoiceLineItem, v: unknown) =>
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l))

  const pickService = (i: number, serviceId: number) => {
    const svc = services.find(s => s.serviceID === serviceId)
    if (!svc) return
    setLines(ls => ls.map((l, idx) => idx === i ? {
      ...l, serviceID: svc.serviceID, title: svc.serviceName,
      detail: svc.serviceDescription ?? '', rate: svc.serviceRate, isTaxable: svc.isTaxable,
    } : l))
  }

  const save = async () => {
    if (!inv.clientID) { setError('Please select a client.'); return }
    if (!lines.length || lines.every(l => !l.title?.trim())) { setError('Add at least one line item.'); return }
    setError('')
    setSaving(true)
    try {
      const body = { invoice: { ...inv, businessID: businessId } as Invoice, lineItems: lines as InvoiceLineItem[] }
      if (mode === 'create') await createInvoice(body)
      else await updateInvoice(initial!.invoiceID, body)
      onSaved()
    } catch { setError('Failed to save invoice. Please try again.') }
    finally { setSaving(false) }
  }

  // Keep due date in sync when invoice date or terms change (preserves prior UX).
  const setDate = (d: string) => {
    set('date', d)
    if (inv.paymentTerms) { const due = autoCalcDueDate(d, inv.paymentTerms); if (due) set('dueDate', due) }
  }
  const setTerms = (terms: string) => {
    set('paymentTerms', terms)
    if (inv.date) { const due = autoCalcDueDate(inv.date.slice(0, 10), terms); if (due) set('dueDate', due) }
  }

  const wasSent    = mode === 'edit' && (!!initial?.sentDate || ['Sent', 'Paid', 'Overdue'].includes(initial?.status ?? ''))
  const isPaid     = mode === 'edit' && initial?.status === 'Paid'
  const paidLinked = !!initial?.paymentTransactionId

  const sideLabel: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)', marginBottom: '0.25rem' }

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pb-muted)', fontSize: '1rem', padding: 0 }}>
            <i className="bi bi-arrow-left" />
          </button>
          <h1 className="pb-page-title mb-0">{mode === 'create' ? 'New Invoice' : `Edit ${initial?.invoiceNumber}`}</h1>
          {mode === 'edit' && initial?.status && <span style={statusBadge(initial.status)}>{initial.status}</span>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onCancel} className="pb-btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="pb-btn-primary">
            {saving ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> : mode === 'create' ? 'Save Draft' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.65rem 1rem', borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#dc2626', fontSize: '0.88rem', marginBottom: '1rem', border: '1px solid rgba(239,68,68,0.2)' }}>
          <i className="bi bi-exclamation-circle me-2" />{error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Paper */}
        <div style={{ flex: '1 1 600px', minWidth: 0 }}>
          <InvoicePaper
            editable
            inv={inv}
            lines={lines}
            business={business}
            clients={clients}
            services={services}
            invoiceNumber={mode === 'edit' ? initial?.invoiceNumber : undefined}
            set={(k, v) => k === 'date' ? setDate(v as string) : k === 'paymentTerms' ? setTerms(v as string) : set(k, v)}
            setLine={setLine}
            pickService={pickService}
            addLine={addLine}
            removeLine={removeLine}
          />
        </div>

        {/* Slim side panel — fields that don't appear on the printed invoice */}
        <div style={{ flex: '0 0 250px', width: 250, position: 'sticky', top: 80 }}>
          <div className="pb-card">
            <div className="pb-kpi-label mb-3">Details</div>
            <div className="mb-3">
              <div style={sideLabel}>Payment Method</div>
              <input className={inputCls} style={{ width: '100%' }} placeholder="Bank transfer / EFT" value={inv.paymentMethod ?? ''} onChange={e => set('paymentMethod', e.target.value)} />
            </div>
            <div className="mb-3">
              <div style={sideLabel}>Currency</div>
              <input className={inputCls} style={{ width: '100%' }} placeholder="CAD" value={inv.currency ?? 'CAD'} onChange={e => set('currency', e.target.value.toUpperCase())} />
            </div>

            {(wasSent || isPaid) && <div style={{ borderTop: '1px solid var(--pb-border)', margin: '0.25rem 0 1rem' }} />}
            {wasSent && (
              <div className="mb-3">
                <div style={sideLabel}>Sent Date</div>
                <input type="date" className={inputCls} style={{ width: '100%' }}
                  value={inv.sentDate?.slice(0, 10) ?? ''}
                  onChange={e => set('sentDate', e.target.value || null)} />
              </div>
            )}
            {isPaid && (
              <div className="mb-1">
                <div style={sideLabel}>Paid Date</div>
                <input type="date" className={inputCls} style={{ width: '100%' }}
                  value={(paidLinked ? initial?.paidTransactionDate : inv.paidDate)?.slice(0, 10) ?? ''}
                  disabled={paidLinked}
                  onChange={e => set('paidDate', e.target.value || null)} />
                {paidLinked && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', marginTop: 4 }}>
                    <i className="bi bi-link-45deg me-1" />Set by the linked transaction — unlink to edit.
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', marginTop: '0.6rem', lineHeight: 1.5, padding: '0 0.25rem' }}>
            <i className="bi bi-info-circle me-1" />Click any underlined field on the invoice to edit it directly.
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Log Payment Modal ─────────────────────────────────────────────────────────
function LogPaymentModal({ inv, onConfirm, onClose }: {
  inv: Invoice
  onConfirm: (transactionId: string | null) => Promise<void>
  onClose: () => void
}) {
  const [search, setSearch]           = useState('')
  const [selected, setSelected]       = useState<string | null>(inv.paymentTransactionId ?? null)
  const [txList, setTxList]           = useState<Transaction[]>([])
  const [linkedTxIds, setLinkedTxIds] = useState<Set<string>>(new Set())
  const [loading, setLoading]         = useState(true)
  const [confirming, setConfirming]   = useState(false)
  const [viewMode, setViewMode]       = useState<'all' | 'unlinked' | 'exact'>('all')

  const invoiceTotal = calcTotals(inv.lineItems ?? [], inv.taxRate).total
  const isPaid = inv.status === 'Paid'  // re-linking an already-paid invoice vs first-time payment

  // Fetch business Revenue transactions + the set of transactions already linked
  // to other invoices (so we can offer an "unlinked only" view).
  useEffect(() => {
    setLoading(true)
    Promise.all([
      searchTransactions({ isPersonal: false, categories: 'Revenue' }),
      getInvoices(inv.businessID),
    ])
      .then(([txRes, invs]) => {
        setTxList(txRes.items ?? [])
        // Linked elsewhere = linked by any invoice other than this one.
        const ids = new Set<string>()
        for (const other of invs) {
          if (other.invoiceID !== inv.invoiceID && other.paymentTransactionId)
            ids.add(other.paymentTransactionId)
        }
        setLinkedTxIds(ids)
      })
      .catch(() => { setTxList([]); setLinkedTxIds(new Set()) })
      .finally(() => setLoading(false))
  }, [inv.businessID, inv.invoiceID])

  const filtered = txList.filter(t => {
    if (viewMode === 'unlinked' && linkedTxIds.has(t.id)) return false
    if (viewMode === 'exact' && Math.abs(Math.abs(t.charges ?? 0) - invoiceTotal) > 0.005) return false
    const q = search.toLowerCase()
    return !q
      || (t.merchantDisplayName ?? '').toLowerCase().includes(q)
      || (t.date ?? '').includes(q)
      || String(Math.abs(t.charges ?? 0)).includes(q)
  })

  const money = (v: number | null) =>
    v == null ? '' : Math.abs(v).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })

  const handleConfirmWith = async (txId: string | null) => {
    setConfirming(true)
    try { await onConfirm(txId) }
    finally { setConfirming(false) }
  }
  const handleConfirm = () => handleConfirmWith(selected)

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div className="modal fade show" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60, paddingBottom: 24, zIndex: 1050 }}
        onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="modal-dialog" style={{ margin: 0, width: '95%', maxWidth: 680, display: 'flex', flexDirection: 'column' }}>
          <div className="modal-content" style={{ background: 'var(--pb-glass-bg-strong)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', color: 'var(--pb-text)', border: '1px solid var(--pb-border)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 100px)' }}>

            {/* Header */}
            <div className="modal-header" style={{ borderBottom: '1px solid var(--pb-border)', background: 'transparent' }}>
              <div>
                <h5 className="modal-title mb-0" style={{ fontWeight: 700 }}>
                  <i className="bi bi-cash-coin me-2 text-success" />{isPaid ? 'Link Transaction' : 'Log Payment'}
                </h5>
                <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: 2 }}>
                  {inv.invoiceNumber} · {fmt(calcTotals(inv.lineItems ?? [], inv.taxRate).total)} due
                </div>
              </div>
              <button className="btn-close" onClick={onClose} />
            </div>

            {/* Body */}
            <div className="modal-body" style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: '1 1 auto' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--pb-muted)', marginBottom: '1rem' }}>
                {isPaid
                  ? <>Choose the <strong>Revenue</strong> transaction this paid invoice should be linked to. The paid date &amp; amount come from the linked transaction.</>
                  : <>Optionally link a <strong>Revenue</strong> transaction to this payment. Skip if no matching transaction exists yet.</>}
              </p>

              {/* View filters */}
              <div className="pb-chart-selector" style={{ display: 'inline-flex', marginBottom: '0.75rem' }}>
                <button className={`pb-chart-sel-btn${viewMode === 'all' ? ' active' : ''}`} onClick={() => setViewMode('all')}>All</button>
                <button className={`pb-chart-sel-btn${viewMode === 'unlinked' ? ' active' : ''}`} onClick={() => setViewMode('unlinked')}>
                  Unlinked only
                </button>
                <button className={`pb-chart-sel-btn${viewMode === 'exact' ? ' active' : ''}`} onClick={() => setViewMode('exact')}>
                  Exact amount ({fmt(invoiceTotal)})
                </button>
              </div>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                <i className="bi bi-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--pb-muted)', fontSize: '0.85rem' }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Filter by merchant, date, or amount…"
                  style={{ width: '100%', padding: '0.55rem 0.75rem 0.55rem 2.2rem', borderRadius: 10, border: '1px solid var(--pb-border)', background: 'var(--pb-card-bg,#fff)', color: 'var(--pb-text)', fontSize: '0.85rem', outline: 'none' }}
                />
              </div>

              {/* Transaction list */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--pb-muted)' }}>
                  <span className="spinner-border spinner-border-sm me-2" />Loading transactions…
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--pb-muted)', fontSize: '0.85rem' }}>
                  {search ? 'No matching transactions' : 'No Revenue transactions found for this business.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {filtered.map(t => {
                    const isSel = selected === t.id
                    return (
                      <div key={t.id} onClick={() => setSelected(isSel ? null : t.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          padding: '0.6rem 0.85rem', borderRadius: 10, cursor: 'pointer',
                          border: `1.5px solid ${isSel ? 'rgba(99,102,241,0.45)' : 'var(--pb-border)'}`,
                          background: isSel ? 'rgba(99,102,241,0.06)' : 'var(--pb-card-bg,#fff)',
                          transition: 'all 0.12s',
                        }}>
                        {/* Radio dot */}
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                          border: `2px solid ${isSel ? '#6366f1' : 'var(--pb-border)'}`,
                          background: isSel ? '#6366f1' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {isSel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.merchantDisplayName || '—'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>
                            {t.date} {t.category && <span style={{ marginLeft: '0.5rem', color: '#059669' }}>{t.category}</span>}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, color: '#059669', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                          {money(t.charges)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ borderTop: '1px solid var(--pb-border)', background: 'transparent', gap: '0.5rem' }}>
              {selected && (
                <span style={{ fontSize: '0.8rem', color: '#059669', marginRight: 'auto' }}>
                  <i className="bi bi-link-45deg me-1" />Transaction linked
                </span>
              )}
              <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
              {isPaid ? (
                <>
                  {inv.paymentTransactionId && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleConfirmWith(null)} disabled={confirming}>
                      {confirming ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : 'Unlink'}
                    </button>
                  )}
                  <button
                    className="btn btn-sm"
                    onClick={handleConfirm}
                    disabled={confirming || !selected}
                    style={{ background: 'var(--pb-primary)', color: '#fff', fontWeight: 600, border: 'none' }}>
                    {confirming
                      ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                      : <><i className="bi bi-link-45deg me-1" />Save Link</>
                    }
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => handleConfirmWith(null)} disabled={confirming}>
                    {confirming ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : 'Skip — Mark Paid Without Linking'}
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={handleConfirm}
                    disabled={confirming}
                    style={{ background: '#059669', color: '#fff', fontWeight: 600, border: 'none' }}>
                    {confirming
                      ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                      : <><i className="bi bi-cash-coin me-1" />{selected ? 'Mark Paid & Link' : 'Skip — Mark Paid'}</>
                    }
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Invoice Detail ────────────────────────────────────────────────────────────
function InvoiceDetail({ invoiceId, onBack, onEdit, onDeleted }: { invoiceId: number; onBack: () => void; onEdit: () => void; onDeleted: () => void }) {
  const qc = useQueryClient()
  const [acting, setActing] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    staleTime: 0,
  })

  if (isLoading || !inv) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    qc.invalidateQueries({ queryKey: ['invoices'] })
  }

  const handleAction = async (action: () => Promise<void>, label: string) => {
    if (!window.confirm(`${label} — are you sure?`)) return
    setActing(true)
    try { await action(); refresh() }
    catch { alert(`Failed: ${label}`) }
    finally { setActing(false) }
  }

  const handlePdf = async () => {
    setPdfLoading(true)
    try {
      const blob = await downloadInvoicePdf(inv.invoiceID)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `${inv.invoiceNumber}.pdf`
      document.body.appendChild(a); a.click()
      document.body.removeChild(a); URL.revokeObjectURL(url)
    } catch (e) { alert('Failed to generate PDF: ' + (e instanceof Error ? e.message : String(e))) }
    finally { setPdfLoading(false) }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice ${inv.invoiceNumber}?`)) return
    await deleteInvoice(inv.invoiceID); refresh(); onDeleted()
  }

  const actionBtn = (() => {
    if (inv.status === 'Draft') return (
      <button onClick={() => handleAction(() => approveInvoice(inv.invoiceID), 'Approve invoice')} disabled={acting}
        style={{ padding: '0.45rem 1rem', borderRadius: 10, border: 'none', background: 'rgba(251,191,36,0.15)', color: '#b45309', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
        <i className="bi bi-check2-circle me-1" />Approve
      </button>
    )
    if (inv.status === 'Approved') return (
      <button onClick={() => handleAction(() => markSentInvoice(inv.invoiceID), 'Mark as Sent')} disabled={acting}
        style={{ padding: '0.45rem 1rem', borderRadius: 10, border: 'none', background: 'rgba(59,130,246,0.15)', color: '#1d4ed8', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
        <i className="bi bi-send me-1" />Mark Sent
      </button>
    )
    if (inv.status === 'Sent' || inv.status === 'Overdue') return (
      <button onClick={() => setShowPayModal(true)} disabled={acting}
        style={{ padding: '0.45rem 1rem', borderRadius: 10, border: 'none', background: 'rgba(16,185,129,0.15)', color: '#059669', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
        <i className="bi bi-cash-coin me-1" />Log Payment
      </button>
    )
    if (inv.status === 'Paid') return (
      <button onClick={() => setShowPayModal(true)} disabled={acting}
        style={{ padding: '0.45rem 1rem', borderRadius: 10, border: 'none', background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
        <i className="bi bi-link-45deg me-1" />{inv.paymentTransactionId ? 'Re-link Transaction' : 'Link Transaction'}
      </button>
    )
    return null
  })()

  const handleLogPayment = async (transactionId: string | null) => {
    setActing(true)
    try {
      await logPayment(inv.invoiceID, transactionId)
      setShowPayModal(false)
      refresh()
    } catch { alert('Failed to log payment') }
    finally { setActing(false) }
  }

  return (
    <>
    {showPayModal && (
      <LogPaymentModal
        inv={inv}
        onConfirm={handleLogPayment}
        onClose={() => setShowPayModal(false)}
      />
    )}
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pb-muted)', fontSize: '1rem', padding: 0 }}>
            <i className="bi bi-arrow-left" />
          </button>
          <div>
            <h1 className="pb-page-title mb-0">{inv.invoiceNumber}</h1>
            <span style={statusBadge(inv.status)}>{inv.status}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {actionBtn}
          <button onClick={handlePdf} disabled={pdfLoading} className="pb-btn-ghost" style={{ fontSize: '0.84rem' }}>
            {pdfLoading ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <><i className="bi bi-file-earmark-pdf me-1" />PDF</>}
          </button>
          <button onClick={onEdit} className="pb-btn-ghost" style={{ fontSize: '0.84rem' }}>
            <i className="bi bi-pencil me-1" />Edit
          </button>
          <button onClick={handleDelete} className="pb-btn-ghost" style={{ fontSize: '0.84rem', color: 'var(--pb-danger)' }}>
            <i className="bi bi-trash me-1" />Delete
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Paper — read-only rendition matching the exported PDF */}
        <div style={{ flex: '1 1 600px', minWidth: 0 }}>
          <InvoicePaper
            editable={false}
            inv={inv}
            lines={inv.lineItems ?? []}
            business={inv.business}
            invoiceNumber={inv.invoiceNumber}
          />
        </div>

        {/* Slim side panel — payment meta that isn't printed on the invoice */}
        {(() => {
          const rows: [string, React.ReactNode][] = [
            ['Method', inv.paymentMethod],
            ['Sent', fmtDate(inv.sentDate) || null],
            ['Paid', fmtDate(paidDateOf(inv)) || null],
            ['Paid Amount', paidAmountOf(inv) != null ? fmt(paidAmountOf(inv)!) : null],
          ].filter(r => r[1]) as [string, React.ReactNode][]
          if (!rows.length) return null
          return (
            <div style={{ flex: '0 0 250px', width: 250, position: 'sticky', top: 80 }}>
              <div className="pb-card">
                <div className="pb-kpi-label mb-3">Payment</div>
                {rows.map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: 'var(--pb-muted)' }}>{k}</span>
                    <span style={{ fontWeight: 600, textAlign: 'right' }}>
                      {v}{k === 'Paid' && inv.paymentTransactionId && <i className="bi bi-link-45deg ms-1" title="From linked transaction" style={{ color: 'var(--pb-primary)' }} />}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })()}
      </div>
    </div>
    </>
  )
}

type InvSortF = 'number' | 'client' | 'date' | 'amount' | 'paidDate' | 'paidAmount' | 'status'

const STATUS_TABS = ['All', 'Draft', 'Approved', 'Sent', 'Paid', 'Overdue'] as const

export default function BizInvoices() {
  const [view, setView] = useState<View>('list')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('All')
  const [sortKeys, setSortKeys] = useState<SortKey<InvSortF>[]>([{ field: 'date', dir: 'desc' }])
  const [selectedFY, setSelectedFY] = useState<number | null>(null)
  const [chartMetric, setChartMetric] = useState<'invoiced' | 'paid'>('invoiced')
  const [selectedClient, setSelectedClient] = useState<string | null>(null)

  const { data: businesses } = useQuery({ queryKey: ['businesses'], queryFn: getBusinesses, staleTime: 60_000 })
  const biz = businesses?.[0] ?? null

  const { data: fySettings } = useQuery({ queryKey: ['fiscal-year-settings'], queryFn: getFiscalYearSettings, staleTime: 300_000 })
  const fy: FiscalYearSettings = fySettings ?? { followsCalendar: true, startMonth: 1, startDay: 1 }
  const currentFY = getFiscalYear(new Date().toISOString().slice(0, 10), fy)

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices', biz?.businessID],
    queryFn: () => getInvoices(biz!.businessID),
    enabled: !!biz, staleTime: 15_000,
  })

  // FY options come strictly from the fiscal years present in Transaction data.
  const { data: txFyYears = [] } = useQuery({
    queryKey: ['fy-years-biz', biz?.businessID],
    queryFn: () => getTransactionFYYears(false, biz!.businessID),
    enabled: !!biz, staleTime: 5 * 60_000,
  })
  // Always include the current FY so a brand-new period is selectable.
  const fyYears = Array.from(new Set<number>([currentFY, ...txFyYears])).sort((a, b) => b - a)

  // Default the FY selection to the current fiscal year once settings load.
  useEffect(() => { if (selectedFY === null) setSelectedFY(currentFY) }, [currentFY]) // eslint-disable-line react-hooks/exhaustive-deps

  // Client name as used by the chart datasets (so a chart selection matches here).
  const clientOf = (inv: Invoice) => inv.client?.clientName ?? '—'
  const clientMatch = (inv: Invoice) => selectedClient === null || clientOf(inv) === selectedClient
  // Base set with the chart's client filter applied (affects KPIs, counts and table).
  const clientInvoices = invoices.filter(clientMatch)

  // FY scoping affects ONLY Paid invoices: non-paid invoices always show; paid
  // invoices show only when their paid date falls in the selected FY ('All' = no filter).
  const inSelectedFY = (inv: Invoice): boolean => {
    if (selectedFY === null) return true
    return fyOf(paidDateOf(inv), fy) === selectedFY
  }
  const fyScopedInvoices = clientInvoices.filter(inv => inv.status !== 'Paid' || inSelectedFY(inv))

  const filteredInvoices = statusFilter === 'All' ? fyScopedInvoices : fyScopedInvoices.filter(inv => inv.status === statusFilter)

  // Counts: Draft/Approved/Sent/Overdue are FY-independent (but respect the client filter);
  // Paid reflects the FY filter; All = total displayed.
  const statusCounts = Object.fromEntries(
    STATUS_TABS.map(s => {
      if (s === 'All')  return [s, fyScopedInvoices.length]
      if (s === 'Paid') return [s, fyScopedInvoices.filter(inv => inv.status === 'Paid').length]
      return [s, clientInvoices.filter(inv => inv.status === s).length]
    })
  ) as Record<typeof STATUS_TABS[number], number>

  // ── KPIs (FY- and client-scoped) ─────────────────────────────────────────────
  // Total Invoiced: Sent invoices, by sent date in the selected FY.
  const totalInvoiced = clientInvoices
    .filter(inv => inv.status === 'Sent' && (selectedFY === null || fyOf(inv.sentDate, fy) === selectedFY))
    .reduce((s, inv) => s + calcTotals(inv.lineItems ?? [], inv.taxRate).total, 0)
  // Total Paid: Paid invoices, by paid date in the selected FY.
  const totalPaid = clientInvoices
    .filter(inv => inv.status === 'Paid' && (selectedFY === null || fyOf(paidDateOf(inv), fy) === selectedFY))
    .reduce((s, inv) => s + (paidAmountOf(inv) ?? 0), 0)

  // ── Chart: invoiced / paid amount per month, stacked by client ───────────────
  const chartData = (() => {
    const useInvoiced = chartMetric === 'invoiced'
    // Source rows: {date, amount, clientName} scoped to the selected FY.
    type Row = { date: string; amount: number; client: string }
    const rows: Row[] = []
    for (const inv of invoices) {
      const clientName = inv.client?.clientName ?? '—'
      if (useInvoiced) {
        const d = inv.date
        if (!d) continue
        if (selectedFY !== null && fyOf(d, fy) !== selectedFY) continue
        rows.push({ date: d.slice(0, 10), amount: calcTotals(inv.lineItems ?? [], inv.taxRate).total, client: clientName })
      } else {
        if (inv.status !== 'Paid') continue
        const d = paidDateOf(inv)
        if (!d) continue
        if (selectedFY !== null && fyOf(d, fy) !== selectedFY) continue
        rows.push({ date: d.slice(0, 10), amount: paidAmountOf(inv) ?? 0, client: clientName })
      }
    }
    if (rows.length === 0) return null

    const [rangeFrom, rangeTo] = selectedFY !== null
      ? getFYDateRange(selectedFY, fy)
      : [rows.reduce((m, r) => r.date < m ? r.date : m, rows[0].date),
         rows.reduce((m, r) => r.date > m ? r.date : m, rows[0].date)]
    const buckets = monthBuckets(rangeFrom, rangeTo)
    const clientNames = Array.from(new Set(rows.map(r => r.client)))

    const datasets = clientNames.map((name, i) => {
      const byMonth: Record<string, number> = {}
      for (const r of rows) {
        if (r.client !== name) continue
        const key = r.date.slice(0, 7)
        byMonth[key] = (byMonth[key] ?? 0) + r.amount
      }
      const dim = selectedClient !== null && name !== selectedClient
      return {
        label: name,
        data: buckets.map(b => byMonth[b.key] ?? 0),
        backgroundColor: dim ? 'rgba(148,163,184,0.25)' : CLIENT_COLORS[i % CLIENT_COLORS.length],
        borderRadius: 4,
        borderSkipped: false as const,
        stack: 'clients',
      }
    })
    return { labels: buckets.map(b => b.label), datasets }
  })()

  const invValue = (inv: Invoice, f: InvSortF): string | number => {
    switch (f) {
      case 'number':     return inv.invoiceNumber
      case 'client':     return (inv.client?.clientName ?? '').toLowerCase()
      case 'date':       return inv.date ?? ''
      case 'amount':     return calcTotals(inv.lineItems ?? [], inv.taxRate).total
      case 'paidDate':   return paidDateOf(inv) ?? ''
      case 'paidAmount': return paidAmountOf(inv) ?? -1
      case 'status':     return inv.status
    }
  }
  const sortedInvoices = multiSortBy(filteredInvoices, sortKeys, invValue)
  const handleSort = (f: InvSortF) => setSortKeys(prev => promoteSort(prev, f, ['date']))

  const { data: clients = [] } = useQuery({
    queryKey: ['biz-clients', biz?.businessID],
    queryFn: () => getClients(biz!.businessID),
    enabled: !!biz, staleTime: 60_000,
  })
  const { data: services = [] } = useQuery({
    queryKey: ['biz-services', biz?.businessID],
    queryFn: () => getBizServices(biz!.businessID),
    enabled: !!biz, staleTime: 60_000,
  })

  const qc = useQueryClient()
  const refreshList = () => { qc.invalidateQueries({ queryKey: ['invoices'] }); setView('list') }

  if (!biz && !isLoading) return (
    <div>
      <h1 className="pb-page-title mb-3">Invoices</h1>
      <div className="pb-card">
        <div className="pb-empty">
          <i className="bi bi-briefcase" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
          Set up your business first.<br />
          <small>Go to Settings → Business to configure your profile, clients and services.</small>
        </div>
      </div>
    </div>
  )

  if (view === 'create') return (
    <div>
      <InvoiceForm mode="create" businessId={biz!.businessID} business={biz!} clients={clients} services={services}
        taxRate={biz!.taxRate} defaultPaymentTerms={biz!.defaultPaymentTerms ?? 'Net 30'} defaultPaymentMethod={biz!.defaultPaymentMethod ?? ''}
        onSaved={refreshList} onCancel={() => setView('list')} />
    </div>
  )

  if (view === 'edit' && selectedId) return (
    <div>
      <InvoiceFormLoader invoiceId={selectedId} businessId={biz!.businessID} business={biz!} clients={clients} services={services}
        taxRate={biz!.taxRate} defaultPaymentTerms={biz!.defaultPaymentTerms ?? 'Net 30'} defaultPaymentMethod={biz!.defaultPaymentMethod ?? ''}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['invoice', selectedId] }); setView('detail') }}
        onCancel={() => setView('detail')} />
    </div>
  )

  if (view === 'detail' && selectedId) return (
    <div>
      <InvoiceDetail invoiceId={selectedId} onBack={() => setView('list')} onEdit={() => setView('edit')} onDeleted={refreshList} />
    </div>
  )

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div>
      <h1 className="pb-page-title mb-3">Invoices</h1>

      {/* FY selector — scopes only the Paid invoices below */}
      <div className="mb-2">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {fyYears.map(y => (
            <button key={y}
              className={`pb-chart-sel-btn${selectedFY === y ? ' active' : ''}`}
              onClick={() => setSelectedFY(y)}>
              {fyLabel(y, fy.followsCalendar)}
            </button>
          ))}
          <button
            className={`pb-chart-sel-btn${selectedFY === null ? ' active' : ''}`}
            onClick={() => setSelectedFY(null)}>
            All
          </button>
        </div>
        <span style={{ marginLeft: '0.75rem', fontSize: '0.78rem', color: 'var(--pb-muted)' }}>
          <i className="bi bi-info-circle me-1" />Filters Paid invoices only
        </span>
      </div>

      {/* Active client filter chip */}
      {selectedClient && (
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.7rem', borderRadius: 999, background: 'rgba(99,102,241,0.12)', color: 'var(--pb-primary)', fontSize: '0.8rem', fontWeight: 600 }}>
            <i className="bi bi-funnel-fill" />Client: {selectedClient}
            <button onClick={() => setSelectedClient(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'inline-flex' }} title="Clear client filter">
              <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
            </button>
          </span>
        </div>
      )}

      {/* KPIs */}
      <div className="row g-3 mb-3">
        <div className="col-sm-6">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-send me-1" />Total Unpaid</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1d4ed8' }}>{fmt(totalInvoiced)}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--pb-muted)' }}>Sent invoices · by sent date{selectedFY !== null ? ` · ${fyLabel(selectedFY, fy.followsCalendar)}` : ''}</div>
          </div>
        </div>
        <div className="col-sm-6">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-cash-coin me-1" />Total Paid</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>{fmt(totalPaid)}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--pb-muted)' }}>Paid invoices · by paid date{selectedFY !== null ? ` · ${fyLabel(selectedFY, fy.followsCalendar)}` : ''}</div>
          </div>
        </div>
      </div>

      {/* Monthly breakdown by client */}
      <div className="pb-card mb-3">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div className="pb-kpi-label mb-0">
            <i className="bi bi-bar-chart me-2" />
            {chartMetric === 'invoiced' ? 'Invoiced' : 'Paid'} by Month &amp; Client
            <span className="ms-2" style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--pb-muted)' }}>
              {selectedFY !== null ? fyLabel(selectedFY, fy.followsCalendar) : 'All time'}
            </span>
          </div>
          <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
            <button className={`pb-chart-sel-btn${chartMetric === 'invoiced' ? ' active' : ''}`} onClick={() => setChartMetric('invoiced')}>Invoiced</button>
            <button className={`pb-chart-sel-btn${chartMetric === 'paid' ? ' active' : ''}`} onClick={() => setChartMetric('paid')}>Paid</button>
          </div>
        </div>
        {chartData ? (
          <div style={{ height: 280 }}>
            <Bar
              data={chartData}
              options={{
                responsive: true, maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                onClick: (_evt, elements) => {
                  if (!elements.length) return
                  const name = chartData?.datasets[elements[0].datasetIndex]?.label
                  if (name) setSelectedClient(prev => prev === name ? null : name)
                },
                plugins: {
                  legend: {
                    position: 'bottom' as const,
                    labels: { boxWidth: 12, font: { size: 11 } },
                    onClick: (_e, item) => {
                      const name = item.text
                      if (name) setSelectedClient(prev => prev === name ? null : name)
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y ?? 0)}`,
                      footer: (items) => `Total: ${fmt(items.reduce((s, i) => s + ((i.parsed.y as number | null) ?? 0), 0))}`,
                    },
                  },
                },
                scales: {
                  x: { stacked: true, grid: { display: false } },
                  y: { stacked: true, ticks: { callback: (v) => fmt(Number(v)) }, grid: { color: 'rgba(148,163,184,0.15)' } },
                },
              }}
            />
          </div>
        ) : (
          <div className="pb-empty" style={{ padding: '2rem' }}>
            <i className="bi bi-bar-chart" style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem' }} />
            No {chartMetric === 'invoiced' ? 'invoiced' : 'paid'} data for this period.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        {/* Status tabs */}
        <div className="pb-month-pills" style={{ display: 'inline-flex' }}>
          {STATUS_TABS.map(s => (
            <button key={s}
              className={`pb-month-pill${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}>
              {s}
              {statusCounts[s as typeof STATUS_TABS[number]] > 0 && (
                <span style={{
                  marginLeft: '0.35rem',
                  background: statusFilter === s ? 'rgba(255,255,255,0.35)' : 'rgba(99,102,241,0.12)',
                  color: statusFilter === s ? '#fff' : 'var(--pb-primary)',
                  borderRadius: 999, padding: '0.05rem 0.42rem',
                  fontSize: '0.68rem', fontWeight: 700,
                }}>{statusCounts[s as typeof STATUS_TABS[number]]}</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={() => setView('create')} className="pb-btn-primary">
          <i className="bi bi-plus-lg me-1" style={{ fontSize: '0.8rem' }} />New Invoice
        </button>
      </div>

      {isLoading && <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>}

      {!isLoading && filteredInvoices.length === 0 && (
        <div className="pb-card">
          <div className="pb-empty">
            <i className="bi bi-file-earmark-text" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
            No invoices yet.<br /><small>Click "New Invoice" to create your first one.</small>
          </div>
        </div>
      )}

      {/* Invoice rows */}
      {!isLoading && sortedInvoices.length > 0 && (
        <div className="pb-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.85rem 1.25rem 0.7rem', borderBottom: '1px solid var(--pb-border)' }}>
            <div className="pb-kpi-label"><i className="bi bi-file-earmark-text me-2" />Invoices<span className="ms-2" style={{ fontSize: '0.82rem', fontWeight: 400, color: 'var(--pb-muted)' }}>({sortedInvoices.length})</span></div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <SortableTh label="Invoice #"   field="number"     sortKeys={sortKeys} onSort={() => handleSort('number')} />
                  <SortableTh label="Client"      field="client"     sortKeys={sortKeys} onSort={() => handleSort('client')} />
                  <SortableTh label="Date"        field="date"       sortKeys={sortKeys} onSort={() => handleSort('date')} />
                  <SortableTh label="Amount"      field="amount"     sortKeys={sortKeys} onSort={() => handleSort('amount')} right />
                  <SortableTh label="Paid Date"   field="paidDate"   sortKeys={sortKeys} onSort={() => handleSort('paidDate')} />
                  <SortableTh label="Paid Amount" field="paidAmount" sortKeys={sortKeys} onSort={() => handleSort('paidAmount')} right />
                  <SortableTh label="Status"      field="status"     sortKeys={sortKeys} onSort={() => handleSort('status')} />
                  <th style={{ width: 32, padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)' }} />
                </tr>
              </thead>
              <tbody>
                {sortedInvoices.map(inv => {
                  const { total } = calcTotals(inv.lineItems ?? [], inv.taxRate)
                  const pDate = paidDateOf(inv)
                  const pAmt  = paidAmountOf(inv)
                  const linked = !!inv.paymentTransactionId
                  const td: React.CSSProperties = { padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', verticalAlign: 'middle' }
                  return (
                    <tr key={inv.invoiceID}
                      onClick={() => { setSelectedId(inv.invoiceID); setView('detail') }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '' }}>
                      <td style={{ ...td, fontWeight: 700 }}>{inv.invoiceNumber}</td>
                      <td style={{ ...td, fontWeight: 500 }}>{inv.client?.clientName ?? clients.find(c => c.clientID === inv.clientID)?.clientName ?? '—'}</td>
                      <td style={{ ...td, color: 'var(--pb-muted)', whiteSpace: 'nowrap' }}>{fmtDate(inv.date)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</td>
                      <td style={{ ...td, color: 'var(--pb-muted)', whiteSpace: 'nowrap' }}>
                        {pDate ? fmtDate(pDate) : '—'}
                        {linked && pDate && <i className="bi bi-link-45deg ms-1" title="From linked transaction" style={{ color: 'var(--pb-primary)' }} />}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: pAmt != null ? '#059669' : 'var(--pb-muted)', fontWeight: pAmt != null ? 700 : 400 }}>
                        {pAmt != null ? fmt(pAmt) : '—'}
                      </td>
                      <td style={td}><span style={statusBadge(inv.status)}>{inv.status}</span></td>
                      <td style={{ ...td, textAlign: 'right', color: 'var(--pb-muted)', fontSize: '0.82rem' }}><i className="bi bi-chevron-right" /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// Loader wrapper for edit mode
function InvoiceFormLoader({ invoiceId, ...rest }: { invoiceId: number } & Omit<InvoiceFormProps, 'mode' | 'initial'>) {
  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    staleTime: 0,
  })
  if (isLoading || !inv) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
  return <InvoiceForm mode="edit" initial={inv} {...rest} />
}
